/**
 * Agency ads overview (flag connectors.agency_overview, default off): every granted client's synced Meta / Google Ads
 * figures in one read-only table, one row per client or per ad account. Date range, network filter and sort live in
 * the URL. A row opens the existing per-client view (/clients/:id/performance). Nothing here changes a budget or
 * calls a provider: GET /v1/connectors/agency/overview only reads what the 15-minute sync stored.
 * Networks come from the response (AGENCY_OVERVIEW_NETWORKS on the server), so a new network needs no new screen,
 * only its label key `connectors.agencyOverview.network.<id>`.
 */
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { Translate } from '@qm/i18n';
import type { OverviewAccountRow, OverviewClientRow, OverviewFlagCode, OverviewMetrics } from '@qm/contracts/connectors';
import { useSurface } from '../kit/context';
import { useResource } from '../kit/resource';
import { Badge, DataTable, Field, Loadable, Money, Notice, Screen, Tabs, type Column, type Tone } from '../kit/ui';
import { usePartnerApi } from './layout';

const K = 'connectors.agencyOverview';
/** The existing per-client view a row opens. */
export const clientHref = (clientTenantId: string) => `/clients/${encodeURIComponent(clientTenantId)}/performance`;
export const OVERVIEW_PRESETS = ['7', '14', '30', '90'] as const;
export const OVERVIEW_SORTS = ['spend', 'results', 'cpa', 'roas', 'change', 'flags', 'name'] as const;
export type OverviewSort = (typeof OVERVIEW_SORTS)[number];
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const day = (d: Date) => d.toISOString().slice(0, 10);

/** The last `n` days ending today (UTC). */
export function presetRange(now: Date, n: number): { from: string; to: string } {
  return { from: day(new Date(now.getTime() - (n - 1) * 86_400_000)), to: day(now) };
}

/** Range from the URL, falling back to the last 30 days when missing or invalid. */
export function rangeFrom(sp: URLSearchParams, now: Date): { from: string; to: string } {
  const from = sp.get('from') ?? '';
  const to = sp.get('to') ?? '';
  return ISO.test(from) && ISO.test(to) && from <= to ? { from, to } : presetRange(now, 30);
}

type Row = { name: string; current: OverviewMetrics; change: OverviewClientRow['change']; flags: OverviewClientRow['flags'] };
const severity = (flags: Row['flags']) => flags.reduce((s, f) => s + ({ disconnected: 1000, zero_delivery: 100, spend_spike: 10, pending_approvals: 1 }[f.code] ?? 0), 0);
const sortValue = (r: Row, key: OverviewSort): number | string | null => {
  switch (key) {
    case 'spend': return r.current.spendMinor;
    case 'results': return r.current.results;
    case 'cpa': return r.current.cpaMinor;
    case 'roas': return r.current.roas;
    case 'change': return r.change.spend;
    case 'flags': return severity(r.flags);
    default: return r.name.toLocaleLowerCase();
  }
};

/** Stable sort; missing values (no CPA, mixed currency) always go last whatever the direction. */
export function sortRows<T extends Row>(rows: readonly T[], key: OverviewSort, dir: 'asc' | 'desc'): T[] {
  return rows.map((r, i) => ({ r, i })).sort((a, b) => {
    const x = sortValue(a.r, key); const y = sortValue(b.r, key);
    if (x === null && y === null) return a.i - b.i;
    if (x === null) return 1;
    if (y === null) return -1;
    const c = x < y ? -1 : x > y ? 1 : 0;
    return (dir === 'asc' ? c : -c) || a.i - b.i;
  }).map((x) => x.r);
}

export function formatChange(locale: string, v: number | null): string {
  if (v === null) return '-';
  const pct = new Intl.NumberFormat(locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE', { style: 'percent', maximumFractionDigits: 0, signDisplay: 'exceptZero' });
  return pct.format(v);
}
const num = (locale: string, v: number, digits = 0) => new Intl.NumberFormat(locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE', { maximumFractionDigits: digits }).format(v);

const FLAG_TONE: Record<OverviewFlagCode, Tone> = { disconnected: 'bad', zero_delivery: 'bad', spend_spike: 'warn', pending_approvals: 'accent' };
const PACING_TONE: Record<string, Tone> = { ahead: 'warn', behind: 'warn', on_track: 'good', no_baseline: 'neutral' };

function MoneyOr({ minor, currency, t }: { minor: number | null; currency: string | null; t: Translate }) {
  if (minor === null || !currency || currency === 'MIXED') return <span data-testid="mixed-currency">{currency === 'MIXED' ? t(`${K}.mixed`) : '-'}</span>;
  return <Money minor={minor} currency={currency} />;
}

function Flags({ flags, t }: { flags: Row['flags']; t: Translate }) {
  if (!flags.length) return <span>-</span>;
  return <span className="qx-toolbar">{flags.map((f) => <Badge key={f.code} tone={FLAG_TONE[f.code]} testId={`flag-${f.code}`}>{t(`${K}.flag.${f.code}`, { n: f.count ?? 1 })}</Badge>)}</span>;
}

function columns(t: Translate, locale: string, kind: 'clients' | 'accounts'): Column<(OverviewClientRow | (OverviewAccountRow & { clientName: string | null; clientTenantId: string })) & Row>[] {
  return [
    {
      key: 'name', header: t(kind === 'clients' ? `${K}.col.client` : `${K}.col.account`), render: (r) => (
        <span>
          <Link className="qp-name" to={clientHref(r.clientTenantId)} onClick={(e) => e.stopPropagation()} data-testid="overview-open">{r.name}</Link>
          {'accounts' in r ? <span className="qx-toolbar">{r.networks.map((n) => <Badge key={n}>{t(`${K}.network.${n}`)}</Badge>)}</span>
            : <span className="qx-toolbar"><Badge>{t(`${K}.network.${r.network}`)}</Badge><span>{r.clientName ?? r.clientTenantId}</span></span>}
        </span>
      ),
    },
    { key: 'spend', header: t(`${K}.col.spend`), render: (r) => <MoneyOr minor={r.current.spendMinor} currency={r.currency} t={t} /> },
    { key: 'results', header: t(`${K}.col.results`), render: (r) => num(locale, r.current.results, 2) },
    { key: 'cpa', header: t(`${K}.col.cpa`), render: (r) => <MoneyOr minor={r.current.cpaMinor} currency={r.current.cpaMinor === null ? null : r.currency} t={t} /> },
    { key: 'roas', header: t(`${K}.col.roas`), narrow: 'hide', render: (r) => (r.current.roas === null ? '-' : `${num(locale, r.current.roas, 2)}x`) },
    {
      key: 'change', header: t(`${K}.col.change`), narrow: 'hide', render: (r) => (
        <span data-testid="change-spend" title={t(`${K}.changeHint`)}>{t(`${K}.changeValue`, { spend: formatChange(locale, r.change.spend), results: formatChange(locale, r.change.results) })}</span>
      ),
    },
    { key: 'pacing', header: t(`${K}.col.pacing`), narrow: 'hide', render: (r) => <Badge tone={PACING_TONE[r.pacing.state] ?? 'neutral'} testId={`pacing-${r.pacing.state}`}>{t(`${K}.pacing.${r.pacing.state}`, { pct: formatChange(locale, r.pacing.vsPrevious) })}</Badge> },
    { key: 'flags', header: t(`${K}.col.flags`), render: (r) => <Flags flags={r.flags} t={t} /> },
  ];
}

export function AdsOverviewPage() {
  const { t, locale, now } = useSurface();
  const api = usePartnerApi();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { from, to } = rangeFrom(sp, now());
  const network = sp.get('network') ?? '';
  const view = sp.get('view') === 'accounts' ? 'accounts' : 'clients';
  const sort: OverviewSort = (OVERVIEW_SORTS as readonly string[]).includes(sp.get('sort') ?? '') ? (sp.get('sort') as OverviewSort) : 'spend';
  const dir = sp.get('dir') === 'asc' ? 'asc' : 'desc';
  const q = { from, to, ...(network ? { network } : {}) };
  const data = useResource(`ads-overview:${JSON.stringify(q)}`, () => api.adsOverview(q));
  const set = (patch: Record<string, string>) => { const n = new URLSearchParams(sp); for (const [k, v] of Object.entries(patch)) { if (v) n.set(k, v); else n.delete(k); } setSp(n); };
  const open = (clientTenantId: string) => nav(clientHref(clientTenantId));

  return (
    <Screen title={t(`${K}.title`)} subtitle={t(`${K}.subtitle`)} testId="ads-overview">
      <div className="qx-toolbar" data-testid="ads-overview-filters">
        <Field label={t(`${K}.range`)}>
          {(id) => (
            <select id={id} value={OVERVIEW_PRESETS.find((n) => { const p = presetRange(now(), Number(n)); return p.from === from && p.to === to; }) ?? ''}
              onChange={(e) => { if (e.target.value) set(presetRange(now(), Number(e.target.value))); }}>
              <option value="">{t(`${K}.range.custom`)}</option>
              {OVERVIEW_PRESETS.map((n) => <option key={n} value={n}>{t(`${K}.range.last`, { n })}</option>)}
            </select>
          )}
        </Field>
        <Field label={t(`${K}.from`)}>{(id) => <input id={id} type="date" value={from} max={to} onChange={(e) => set({ from: e.target.value, to })} />}</Field>
        <Field label={t(`${K}.to`)}>{(id) => <input id={id} type="date" value={to} min={from} onChange={(e) => set({ from, to: e.target.value })} />}</Field>
        <Field label={t(`${K}.network`)}>
          {(id) => (
            <select id={id} value={network} onChange={(e) => set({ network: e.target.value })}>
              <option value="">{t(`${K}.network.all`)}</option>
              {(data.status === 'ready' ? data.data.networks : network ? [network] : []).map((n) => <option key={n} value={n}>{t(`${K}.network.${n}`)}</option>)}
            </select>
          )}
        </Field>
        <Field label={t(`${K}.sort`)}>
          {(id) => (
            <select id={id} value={`${sort}:${dir}`} onChange={(e) => { const [s, d] = e.target.value.split(':'); set({ sort: s ?? '', dir: d ?? '' }); }}>
              {OVERVIEW_SORTS.flatMap((s) => (['desc', 'asc'] as const).map((d) => <option key={`${s}:${d}`} value={`${s}:${d}`}>{t(`${K}.sort.${s}.${d}`)}</option>))}
            </select>
          )}
        </Field>
      </div>
      <Tabs label={t(`${K}.view`)} active={view} onSelect={(v) => set({ view: v === 'clients' ? '' : v })}
        tabs={[{ id: 'clients', label: t(`${K}.view.clients`) }, { id: 'accounts', label: t(`${K}.view.accounts`) }]} />
      <Notice testId="ads-overview-readonly">{t(`${K}.readOnly`)}</Notice>
      <Loadable resource={data}>
        {(d) => {
          const clients = d.clients.map((c) => ({ ...c, name: c.clientName ?? c.clientTenantId }));
          if (view === 'accounts') {
            const accounts = clients.flatMap((c) => c.accounts.map((a) => ({ ...a, name: a.accountName ?? a.accountRef, clientName: c.clientName, clientTenantId: c.clientTenantId })));
            return <DataTable caption={t(`${K}.view.accounts`)} columns={columns(t, locale, 'accounts')} rows={sortRows(accounts, sort, dir)} rowKey={(r) => r.connectionId}
              empty={t(`${K}.empty`)} onRowClick={(r) => open(r.clientTenantId)} />;
          }
          return <DataTable caption={t(`${K}.view.clients`)} columns={columns(t, locale, 'clients')} rows={sortRows(clients, sort, dir)} rowKey={(r) => r.clientTenantId}
            empty={t(`${K}.empty`)} onRowClick={(r) => open(r.clientTenantId)} />;
        }}
      </Loadable>
    </Screen>
  );
}
